/**
 * Demo discipline data for ?mock=1: a believable report per mock project, built from the
 * same rule ids the engine reports, with a score history so the trend has a shape.
 */
import type {
  DisciplineDriftReport,
  DisciplineOnboardScan,
  DisciplinePack,
  DisciplinePin,
  DisciplineReport,
  DisciplineRuleSummary,
  DisciplineScorePoint,
  DisciplineSendBack,
  DisciplineSessionReport,
  DisciplineState,
  DisciplineWaiver,
  Machine,
  Project,
  Session,
  Violation,
} from '@orgus/protocol';

interface Shape {
  violations: Violation[];
  waived?: number;
  pack?: string;
  rulebook?: 'repo' | 'recommended';
  error?: string;
}

const v = (rule: string, path: string, message: string, severity: 'error' | 'warn' = 'error', line?: number): Violation => ({ rule, path, message, severity, ...(line ? { line } : {}) });

const SHAPES: Shape[] = [
  {
    rulebook: 'repo',
    pack: 'monorepo',
    waived: 9,
    violations: [
      v('design/px-spacing', 'apps/web/src/features/agents/Timeline.tsx', 'Pixel spacing in a padding utility (30 px).', 'error', 183),
      v('docs/stale', 'docs/runbooks/rotate-keys.md', 'Last reviewed 212 days ago (limit 180).', 'warn', 1),
    ],
  },
  {
    rulebook: 'repo',
    pack: 'strict',
    violations: [
      v('release/changeset', '.changeset', '7 changed files under src/ since main (first: src/billing/retry.ts) and no changeset in .changeset/.'),
      v('deps/deep-import', 'services/api/src/routes/invoices.ts', 'Imports @acme/billing/src/retry.ts, which @acme/billing does not export.', 'error', 4),
      v('deps/deep-import', 'services/api/src/routes/refunds.ts', 'Imports @acme/billing/src/ledger.ts, which @acme/billing does not export.', 'error', 3),
      v('structure/readme-sections', 'packages/billing/README.md', 'README has no "## API" section.'),
      v('docs/front-matter', 'docs/adr/0007-idempotency-keys.md', 'Front-matter has no owner, last_reviewed.', 'error', 1),
      v('tests/missing', 'packages/webhooks', 'packages/webhooks has 6 source files and no tests.', 'warn'),
    ],
  },
  {
    rulebook: 'recommended',
    pack: 'recommended',
    violations: [
      v('structure/forbidden-file', 'scripts/deploy.sh.bak', 'scripts/deploy.sh.bak matches a forbidden pattern.'),
      v('structure/forbidden-file', 'config/.env', 'config/.env matches a forbidden pattern.'),
      v('structure/stray-doc', 'src/notes.md', 'src/notes.md is a doc inside src/.'),
      v('structure/unit-shape', 'packages/cli', 'packages/cli has no README.md.'),
      v('tests/missing', 'packages/cli', 'packages/cli has 12 source files and no tests.', 'warn'),
      v('tests/missing', 'packages/sync', 'packages/sync has 4 source files and no tests.', 'warn'),
    ],
  },
  { rulebook: 'recommended', pack: 'recommended', violations: [] },
  { rulebook: 'repo', pack: 'design-system', violations: [], error: 'The checkout C:/work/site is not on disk.' },
];

const PICK = [1, 0, 4, 2, 3];

const RULES: Record<string, string[]> = {
  monorepo: ['structure/root-file', 'structure/unit-shape', 'structure/readme-sections', 'structure/unit-manifest', 'structure/forbidden-file', 'structure/stray-doc', 'deps/deep-import', 'deps/undeclared', 'deps/layer', 'design/raw-colour', 'design/px-spacing', 'design/em-dash', 'docs/front-matter', 'docs/stale', 'docs/index', 'release/changeset', 'tests/missing'],
  strict: ['structure/root-file', 'structure/unit-shape', 'structure/readme-sections', 'structure/forbidden-file', 'structure/stray-doc', 'deps/deep-import', 'docs/front-matter', 'docs/stale', 'release/changeset', 'tests/missing'],
  recommended: ['structure/root-file', 'structure/unit-shape', 'structure/forbidden-file', 'structure/stray-doc', 'deps/deep-import', 'tests/missing'],
};

export function summarize(pack: string, violations: Violation[], waived = 0): DisciplineRuleSummary[] {
  const ids = new Set([...(RULES[pack] ?? RULES.recommended!), ...violations.map((x) => x.rule), 'waivers/invalid', 'waivers/expired']);
  return [...ids].map((rule) => {
    const vs = violations.filter((x) => x.rule === rule);
    return { rule, errors: vs.filter((x) => x.severity === 'error').length, warns: vs.filter((x) => x.severity === 'warn').length, waived: rule === 'design/px-spacing' ? waived : 0 };
  });
}

export function scoreFor(rules: DisciplineRuleSummary[]): number {
  return Math.max(0, 100 - rules.reduce((n, r) => n + Math.min(20, r.errors * 5 + r.warns), 0));
}

const DAY = 86_400_000;

/** A believable onboarding scan for a repo (the demo's answer to "Scan repo"). */
export function mockScan(projectId: string, projectName: string | undefined, machineId: string, at: number, score: number, debt: number): DisciplineOnboardScan {
  return {
    projectId,
    machineId,
    ...(projectName ? { projectName } : {}),
    at,
    durationMs: 2300,
    files: 842,
    units: 4,
    languages: [
      { name: 'TypeScript', files: 512 },
      { name: 'CSS', files: 38 },
      { name: 'JavaScript', files: 12 },
    ],
    packs: [
      { pack: 'orgus/recommended', score: 74, errors: 4, warns: 2, failing: [{ rule: 'structure/forbidden-file', files: 2 }, { rule: 'tests/missing', files: 2 }] },
      { pack: 'orgus/strict', score: 41, errors: 14, warns: 2, failing: [{ rule: 'structure/unit-shape', files: 4 }, { rule: 'structure/readme-sections', files: 3 }, { rule: 'docs/front-matter', files: 9 }] },
      { pack: 'orgus/monorepo', score: 22, errors: 31, warns: 2, failing: [{ rule: 'structure/unit-manifest', files: 4 }, { rule: 'deps/undeclared', files: 7 }] },
      { pack: 'orgus/design-system', score: 63, errors: 7, warns: 2, failing: [{ rule: 'design/raw-colour', files: 6 }] },
      { pack: 'orgus/security-critical', score: 69, errors: 5, warns: 3, failing: [{ rule: 'security/dangerous-call', files: 3 }, { rule: 'security/lockfile', files: 1 }] },
      { pack: 'orgus/typescript', score: 58, errors: 2, warns: 18, failing: [{ rule: 'ts/no-explicit-any', files: 14 }, { rule: 'ts/strict', files: 1 }] },
    ],
    suggested: {
      extends: ['orgus/monorepo', 'orgus/design-system', 'orgus/typescript'],
      yaml: '# Suggested by the Orgus onboarding scan.\nversion: 1\nextends: [orgus/monorepo, orgus/design-system, orgus/typescript]\n',
      score: 18,
      why: ['orgus/monorepo: 4 units in a workspace.', 'orgus/design-system: 120 UI files (components and styles).', 'orgus/typescript: 512 TypeScript files.'],
    },
    baseline: { total: 57, byRule: { 'structure/unit-manifest': 4, 'deps/undeclared': 11, 'design/raw-colour': 9, 'ts/no-explicit-any': 21, 'tests/missing': 2, 'structure/forbidden-file': 2, 'docs/front-matter': 8 } },
    current: { rulebook: 'repo', score, total: debt, byRule: {} },
  };
}
const day = (n: number, now: number) => new Date(Math.floor(now / DAY) * DAY + n * DAY).toISOString().slice(0, 10);

/**
 * Everything beyond the reports: session and agent scores, waivers, the ratchet, a kept
 * rulebook, an org pack with a pin, send-backs, drift scans and an onboarding scan.
 */
export function mockDisciplineExtras(base: DisciplineState, projects: readonly Project[], sessions: readonly Session[], now: number): DisciplineState {
  const first = base.reports.find((r) => !r.error && r.violations.length);
  const p0 = first?.projectId ?? projects[0]?.id ?? 'p0';
  const reports = base.reports.map((r) => {
    if (r !== first) return r;
    // The first repo was onboarded a week ago: its older violations are tracked debt.
    const violations = r.violations.map((x, i) => (i >= 2 ? { ...x, debt: true } : x));
    return { ...r, violations, ratchet: { total: 41, remaining: Math.max(0, violations.length - 2) + 23, fresh: Math.min(2, violations.length), burned: 41 - (Math.max(0, violations.length - 2) + 23), since: now - 7 * DAY }, rulebook: 'hub' as const, packs: ['orgus/recommended@1.0.0', 'orgus/strict@1.0.0', 'orgus/typescript@1.0.0', 'acme/web@1.1.0'] };
  });
  const out: DisciplineReport[] = reports;
  const live = sessions.filter((s) => s.projectId && s.checkoutId).slice(0, 6);
  const sessionReports: DisciplineSessionReport[] = live.map((s, i) => {
    const vs = i % 3 === 1 ? [v('tests/missing', 'packages/webhooks', 'packages/webhooks has 2 source files and no tests.', 'warn'), v('ts/no-explicit-any', 'packages/webhooks/src/retry.ts', 'Explicit any.', 'warn', 41)] : i % 3 === 2 ? [v('design/raw-colour', 'apps/web/src/features/billing/Chart.tsx', 'Raw colour in a chart fill (a hex value).', 'error', 18)] : [];
    const errors = vs.filter((x) => x.severity === 'error').length;
    return {
      sessionId: s.id,
      projectId: s.projectId!,
      machineId: s.machineId,
      checkoutId: s.checkoutId,
      agent: s.model ?? 'Claude Code',
      launched: s.origin === 'orgus',
      title: s.title,
      score: Math.max(0, 100 - errors * 5 - (vs.length - errors)),
      errors,
      warns: vs.length - errors,
      files: 3 + i * 4,
      violations: vs,
      checkedAt: now - (i + 1) * 90_000,
    };
  });
  const sessionHistory: Record<string, DisciplineScorePoint[]> = {};
  sessionReports.forEach((s, i) => {
    sessionHistory[s.sessionId] = Array.from({ length: 8 }, (_, k) => ({ at: now - (8 - k) * 20 * 60_000 - i * 60_000, score: Math.max(0, Math.min(100, s.score + Math.round(Math.sin(k + i) * 6) - (7 - k))), errors: s.errors, warns: s.warns }));
    sessionHistory[s.sessionId]!.push({ at: s.checkedAt, score: s.score, errors: s.errors, warns: s.warns });
  });
  const waivers: DisciplineWaiver[] = [
    { id: 'wvr_1', projectId: p0, rule: 'design/px-spacing', path: 'apps/web/src/features/agents/**', reason: 'Timeline lanes need fixed offsets until the canvas lands', owner: 'srikanth', expires: day(3, now), createdBy: 'owner', createdAt: now - 20 * DAY },
    { id: 'wvr_2', projectId: p0, rule: 'tests/missing', path: 'packages/ui', reason: 'Visual tests cover the components; unit tests follow in November', owner: 'ana', expires: day(26, now), createdBy: 'owner', createdAt: now - 4 * DAY },
    { id: 'wvr_3', projectId: projects[1]?.id ?? p0, rule: 'deps/deep-import', path: 'services/api/src/routes/**', reason: 'Billing exports land with the 2.0 refactor', owner: 'li', expires: day(9, now), createdBy: 'owner', createdAt: now - 12 * DAY, revokedAt: now - DAY, revokedBy: 'owner' },
  ];
  const packs: DisciplinePack[] = [
    { id: 'acme/web', version: '1.0.0', title: 'Acme web', description: 'Acme house rules for web apps: strict, TypeScript and the design system.', kind: 'org', extends: ['orgus/strict', 'orgus/typescript'], settings: { design: { tokens_only: true } }, publishedBy: 'owner', publishedAt: now - 30 * DAY },
    { id: 'acme/web', version: '1.1.0', title: 'Acme web', description: 'Acme house rules for web apps: strict, TypeScript, the design system and no em dash.', kind: 'org', extends: ['orgus/strict', 'orgus/typescript'], settings: { design: { tokens_only: true, no_em_dash: true } }, publishedBy: 'owner', publishedAt: now - 6 * DAY },
  ];
  const pins: DisciplinePin[] = [{ pack: 'acme/web', version: '1.1.0', scope: 'all', enforce: false, by: 'owner', at: now - 6 * DAY }];
  const launched = sessionReports.find((s) => s.launched);
  const sendbacks: DisciplineSendBack[] = launched
    ? [
        { id: 'sb_1', projectId: launched.projectId, sessionId: launched.sessionId, machineId: launched.machineId, violations: [v('tests/missing', 'packages/webhooks', 'packages/webhooks has 2 source files and no tests.', 'warn')], note: 'Add a test for the retry path.', state: 'delivered', createdBy: 'owner', createdAt: now - 50 * 60_000, updatedAt: now - 41 * 60_000 },
        { id: 'sb_2', projectId: launched.projectId, sessionId: launched.sessionId, machineId: launched.machineId, violations: [v('design/raw-colour', 'apps/web/src/features/billing/Chart.tsx', 'Raw colour in a chart fill (a hex value).', 'error', 18)], note: '', state: 'pending', createdBy: 'owner', createdAt: now - 4 * 60_000, updatedAt: now - 4 * 60_000 },
      ]
    : [];
  const drift: DisciplineDriftReport[] = base.reports
    .filter((r) => !r.error)
    .map((r, i) => {
      const items =
        i === 0
          ? [
              { kind: 'stale-doc' as const, path: 'docs/runbooks/rotate-keys.md', message: 'Last reviewed 212 days ago (limit 180).' },
              { kind: 'stale-doc' as const, path: 'docs/architecture/queues.md', message: 'No review date, last changed 260 days ago (limit 180).' },
              { kind: 'orphan-file' as const, path: 'apps/web/src/lib/legacy-format.ts', message: 'Nothing in the repo imports this file and it is not an entry point.' },
              { kind: 'unused-dep' as const, path: 'apps/web/package.json', message: 'lodash is a dependency, but nothing under apps/web/ imports it.' },
              { kind: 'expired-waiver' as const, path: '.orgus/waivers.yaml', message: 'waiver 2 (docs/stale on docs/legacy/**) expired on 2026-10-01.' },
            ]
          : i === 1
            ? [{ kind: 'unused-dep' as const, path: 'services/api/package.json', message: 'node-fetch is a dependency, but nothing under services/api/ imports it.' }]
            : [];
      const counts = { 'stale-doc': 0, 'orphan-file': 0, 'unused-dep': 0, 'expired-waiver': 0 };
      for (const it of items) counts[it.kind]++;
      return { projectId: r.projectId, machineId: r.machineId, projectName: r.projectName, at: now - (i + 9) * 3_600_000, items, counts, durationMs: 1400 + i * 300, trigger: 'schedule' as const };
    });
  const p1 = base.reports.find((r) => !r.error && r !== first) ?? first;
  const scans: DisciplineOnboardScan[] = p1 ? [mockScan(p1.projectId, p1.projectName, p1.machineId, now - 2 * 3_600_000, p1.score, p1.violations.length)] : [];
  const baselines: DisciplineState['baselines'] = first ? { [p0]: { projectId: p0, createdAt: now - 7 * DAY, createdBy: 'owner', total: 41, remaining: out.find((r) => r.projectId === p0)?.ratchet?.remaining ?? 0, byRule: { 'design/px-spacing': 12, 'docs/stale': 4, 'tests/missing': 25 } } } : {};
  const managed: DisciplineState['managed'] = first ? { [p0]: { projectId: p0, text: 'version: 1\nextends: [orgus/strict, orgus/typescript, acme/web]\n\ndocs:\n  max_age_days: 120\n', updatedBy: 'owner', updatedAt: now - 7 * DAY } } : {};
  const withRaw = out.map((r) =>
    r.projectId === p0 && managed[p0]
      ? { ...r, rulebookText: managed[p0]!.text, rulebookRaw: { version: 1, extends: ['orgus/strict', 'orgus/typescript', 'acme/web'], docs: { max_age_days: 120 } } }
      : r.rulebook === 'repo'
        ? { ...r, rulebookText: `version: 1\nextends: orgus/${r.pack ?? 'recommended'}\n`, rulebookRaw: { version: 1, extends: `orgus/${r.pack ?? 'recommended'}` } }
        : r,
  );
  return { ...base, reports: withRaw, sessions: sessionReports, sessionHistory, waivers, baselines, managed, packs, pins, sendbacks, drift, scans, driftSchedule: { hour: 3, enabled: true }, pending: { scans: [], drift: [] }, waiverMaxDays: Object.fromEntries(withRaw.map((r) => [r.projectId, 30])) };
}

export function mockDisciplineState(projects: readonly Project[], machines: readonly Machine[], now: number): DisciplineState {
  const reports: DisciplineReport[] = [];
  const history: Record<string, DisciplineScorePoint[]> = {};
  projects.forEach((p, i) => {
    if (i === projects.length - 1 && projects.length > 3) return; // one project still waiting for its first check
    // A failing repo first, then a healthy one, then one whose check could not run.
    const shape = SHAPES[PICK[i % PICK.length]!]!;
    const pack = shape.pack ?? 'recommended';
    const rules = summarize(pack, shape.violations, shape.waived);
    const score = shape.error ? 0 : scoreFor(rules);
    const machine = machines[i % Math.max(1, machines.length)];
    reports.push({
      projectId: p.id,
      machineId: machine?.id ?? '',
      projectName: p.name,
      path: `C:/work/${p.name}`,
      score,
      rulebook: shape.rulebook ?? 'recommended',
      pack,
      violations: shape.violations,
      rules: shape.error ? [] : rules,
      waived: shape.waived ?? 0,
      filesChecked: 300 + i * 217,
      durationMs: 180 + i * 90,
      checkedAt: now - (i * 4 + 2) * 60_000,
      ...(shape.error ? { error: shape.error } : {}),
    });
    if (!shape.error) {
      const pts: DisciplineScorePoint[] = [];
      for (let k = 11; k >= 0; k--) {
        const drift = Math.round(Math.sin(i * 1.7 + k * 0.9) * 4 + k * (i % 2 ? 1.2 : -0.6));
        const s = Math.max(0, Math.min(100, score - drift));
        pts.push({ at: now - k * 3_600_000 - (i * 4 + 2) * 60_000, score: k === 0 ? score : s, errors: 0, warns: 0 });
      }
      history[p.id] = pts;
    }
  });
  return { reports, history };
}
